// Theme definitions for Finora — Ambient Environment System
// All color values are HSL without the hsl() wrapper

export type ThemeTier = 'free' | 'pro' | 'premium';
export type ThemeCategory = 'light' | 'dark' | 'auto';
export type AmbientAnimation = 
  | 'light-rays' 
  | 'flowing-leaves' 
  | 'ripple' 
  | 'twirling-wind' 
  | 'stars' 
  | 'gradient-wave' 
  | 'zen-ripple' 
  | 'canopy' 
  | 'particles';

export interface ThemePreview {
  primary: string;
  background: string;
  accent: string;
}

export interface ThemeTokens {
  background: string;
  foreground: string;
  card: string;
  'card-foreground': string;
  popover: string;
  'popover-foreground': string;
  primary: string;
  'primary-foreground': string;
  secondary: string;
  'secondary-foreground': string;
  muted: string;
  'muted-foreground': string;
  accent: string;
  'accent-foreground': string;
  success: string;
  'success-foreground': string;
  warning: string;
  'warning-foreground': string;
  danger: string;
  'danger-foreground': string;
  destructive: string;
  'destructive-foreground': string;
  border: string;
  input: string;
  ring: string;
  'sidebar-background': string;
  'sidebar-foreground': string;
  'sidebar-primary': string;
  'sidebar-primary-foreground': string;
  'sidebar-accent': string;
  'sidebar-accent-foreground': string;
  'sidebar-border': string;
  'sidebar-ring': string;
}

export interface ThemeDefinition {
  id: string;
  name: string;
  description: string;
  tier: ThemeTier;
  category: ThemeCategory;
  animation: AmbientAnimation;
  preview: ThemePreview;
  tokens: ThemeTokens;
}

// ─── FREE THEMES ────────────────────────────────────────

const sanctuaryTokens: ThemeTokens = {
  background: '40 20% 96%',
  foreground: '30 10% 18%',
  card: '40 15% 99%',
  'card-foreground': '30 10% 18%',
  popover: '40 15% 99%',
  'popover-foreground': '30 10% 18%',
  primary: '160 35% 26%',
  'primary-foreground': '0 0% 100%',
  secondary: '38 15% 93%',
  'secondary-foreground': '30 12% 22%',
  muted: '35 12% 91%',
  'muted-foreground': '30 8% 42%',
  accent: '42 80% 52%',
  'accent-foreground': '42 80% 10%',
  success: '160 40% 38%',
  'success-foreground': '0 0% 100%',
  warning: '42 80% 52%',
  'warning-foreground': '42 80% 12%',
  danger: '0 55% 58%',
  'danger-foreground': '0 0% 100%',
  destructive: '0 55% 58%',
  'destructive-foreground': '0 0% 100%',
  border: '35 12% 88%',
  input: '35 12% 88%',
  ring: '160 35% 26%',
  'sidebar-background': '30 15% 12%',
  'sidebar-foreground': '40 15% 95%',
  'sidebar-primary': '160 35% 50%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '30 12% 18%',
  'sidebar-accent-foreground': '40 15% 95%',
  'sidebar-border': '30 12% 18%',
  'sidebar-ring': '160 35% 50%',
};

const meadowTokens: ThemeTokens = {
  background: '130 18% 96%',
  foreground: '140 15% 14%',
  card: '130 12% 99%',
  'card-foreground': '140 15% 14%',
  popover: '130 12% 99%',
  'popover-foreground': '140 15% 14%',
  primary: '145 30% 32%',
  'primary-foreground': '0 0% 100%',
  secondary: '128 14% 92%',
  'secondary-foreground': '140 18% 20%',
  muted: '125 10% 90%',
  'muted-foreground': '140 8% 40%',
  accent: '80 45% 48%',
  'accent-foreground': '80 45% 10%',
  success: '145 35% 38%',
  'success-foreground': '0 0% 100%',
  warning: '42 70% 52%',
  'warning-foreground': '42 70% 12%',
  danger: '0 50% 55%',
  'danger-foreground': '0 0% 100%',
  destructive: '0 50% 55%',
  'destructive-foreground': '0 0% 100%',
  border: '128 10% 88%',
  input: '128 10% 88%',
  ring: '145 30% 32%',
  'sidebar-background': '140 20% 10%',
  'sidebar-foreground': '130 12% 95%',
  'sidebar-primary': '145 30% 45%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '140 15% 16%',
  'sidebar-accent-foreground': '130 12% 95%',
  'sidebar-border': '140 15% 16%',
  'sidebar-ring': '145 30% 45%',
};

const oceanTokens: ThemeTokens = {
  background: '190 20% 95%',
  foreground: '200 25% 12%',
  card: '190 15% 99%',
  'card-foreground': '200 25% 12%',
  popover: '190 15% 99%',
  'popover-foreground': '200 25% 12%',
  primary: '185 40% 28%',
  'primary-foreground': '0 0% 100%',
  secondary: '188 18% 92%',
  'secondary-foreground': '200 20% 20%',
  muted: '185 12% 90%',
  'muted-foreground': '200 10% 40%',
  accent: '170 50% 42%',
  'accent-foreground': '170 50% 10%',
  success: '170 40% 38%',
  'success-foreground': '0 0% 100%',
  warning: '42 70% 52%',
  'warning-foreground': '42 70% 12%',
  danger: '0 50% 55%',
  'danger-foreground': '0 0% 100%',
  destructive: '0 50% 55%',
  'destructive-foreground': '0 0% 100%',
  border: '188 12% 87%',
  input: '188 12% 87%',
  ring: '185 40% 28%',
  'sidebar-background': '200 30% 10%',
  'sidebar-foreground': '190 15% 95%',
  'sidebar-primary': '185 40% 45%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '200 22% 16%',
  'sidebar-accent-foreground': '190 15% 95%',
  'sidebar-border': '200 22% 16%',
  'sidebar-ring': '185 40% 45%',
};

const duneTokens: ThemeTokens = {
  background: '32 25% 95%',
  foreground: '28 18% 16%',
  card: '32 18% 98%',
  'card-foreground': '28 18% 16%',
  popover: '32 18% 98%',
  'popover-foreground': '28 18% 16%',
  primary: '28 30% 38%',
  'primary-foreground': '32 25% 98%',
  secondary: '30 18% 92%',
  'secondary-foreground': '28 22% 22%',
  muted: '30 14% 90%',
  'muted-foreground': '28 10% 42%',
  accent: '35 55% 50%',
  'accent-foreground': '35 55% 12%',
  success: '155 35% 40%',
  'success-foreground': '0 0% 100%',
  warning: '38 65% 52%',
  'warning-foreground': '38 65% 12%',
  danger: '5 48% 55%',
  'danger-foreground': '0 0% 100%',
  destructive: '5 48% 55%',
  'destructive-foreground': '0 0% 100%',
  border: '30 14% 87%',
  input: '30 14% 87%',
  ring: '28 30% 38%',
  'sidebar-background': '28 22% 12%',
  'sidebar-foreground': '32 18% 95%',
  'sidebar-primary': '35 45% 52%',
  'sidebar-primary-foreground': '28 22% 10%',
  'sidebar-accent': '28 18% 18%',
  'sidebar-accent-foreground': '32 18% 95%',
  'sidebar-border': '28 18% 18%',
  'sidebar-ring': '35 45% 52%',
};

// ─── PRO THEMES ─────────────────────────────────────────

const twilightTokens: ThemeTokens = {
  background: '225 30% 8%',
  foreground: '220 15% 92%',
  card: '225 25% 12%',
  'card-foreground': '220 15% 92%',
  popover: '225 25% 12%',
  'popover-foreground': '220 15% 92%',
  primary: '220 55% 55%',
  'primary-foreground': '0 0% 100%',
  secondary: '225 20% 16%',
  'secondary-foreground': '220 12% 88%',
  muted: '225 18% 14%',
  'muted-foreground': '220 10% 52%',
  accent: '210 40% 60%',
  'accent-foreground': '210 40% 10%',
  success: '165 40% 45%',
  'success-foreground': '165 40% 8%',
  warning: '45 65% 55%',
  'warning-foreground': '45 65% 10%',
  danger: '355 48% 58%',
  'danger-foreground': '0 0% 100%',
  destructive: '355 48% 58%',
  'destructive-foreground': '0 0% 100%',
  border: '225 22% 18%',
  input: '225 22% 18%',
  ring: '220 55% 55%',
  'sidebar-background': '225 35% 6%',
  'sidebar-foreground': '220 15% 90%',
  'sidebar-primary': '220 50% 60%',
  'sidebar-primary-foreground': '225 35% 5%',
  'sidebar-accent': '225 25% 12%',
  'sidebar-accent-foreground': '220 15% 90%',
  'sidebar-border': '225 25% 12%',
  'sidebar-ring': '220 50% 60%',
};

const auroraTokens: ThemeTokens = {
  background: '230 28% 97%',
  foreground: '230 22% 15%',
  card: '230 20% 99%',
  'card-foreground': '230 22% 15%',
  popover: '230 20% 99%',
  'popover-foreground': '230 22% 15%',
  primary: '220 55% 52%',
  'primary-foreground': '0 0% 100%',
  secondary: '230 20% 93%',
  'secondary-foreground': '230 25% 22%',
  muted: '230 15% 91%',
  'muted-foreground': '230 12% 40%',
  accent: '270 45% 62%',
  'accent-foreground': '270 45% 12%',
  success: '160 40% 42%',
  'success-foreground': '0 0% 100%',
  warning: '45 70% 55%',
  'warning-foreground': '45 70% 12%',
  danger: '350 50% 58%',
  'danger-foreground': '0 0% 100%',
  destructive: '350 50% 58%',
  'destructive-foreground': '0 0% 100%',
  border: '230 18% 89%',
  input: '230 18% 89%',
  ring: '220 55% 52%',
  'sidebar-background': '230 30% 12%',
  'sidebar-foreground': '230 18% 95%',
  'sidebar-primary': '220 50% 58%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '230 25% 18%',
  'sidebar-accent-foreground': '230 18% 95%',
  'sidebar-border': '230 25% 18%',
  'sidebar-ring': '220 50% 58%',
};

const zenGardenTokens: ThemeTokens = {
  background: '40 10% 95%',
  foreground: '30 8% 18%',
  card: '40 8% 98%',
  'card-foreground': '30 8% 18%',
  popover: '40 8% 98%',
  'popover-foreground': '30 8% 18%',
  primary: '35 20% 40%',
  'primary-foreground': '40 10% 98%',
  secondary: '38 10% 91%',
  'secondary-foreground': '30 12% 25%',
  muted: '35 8% 89%',
  'muted-foreground': '30 6% 45%',
  accent: '165 25% 48%',
  'accent-foreground': '165 25% 10%',
  success: '160 30% 42%',
  'success-foreground': '0 0% 100%',
  warning: '42 55% 52%',
  'warning-foreground': '42 55% 12%',
  danger: '0 40% 55%',
  'danger-foreground': '0 0% 100%',
  destructive: '0 40% 55%',
  'destructive-foreground': '0 0% 100%',
  border: '38 8% 86%',
  input: '38 8% 86%',
  ring: '35 20% 40%',
  'sidebar-background': '30 12% 11%',
  'sidebar-foreground': '40 8% 92%',
  'sidebar-primary': '165 25% 52%',
  'sidebar-primary-foreground': '30 12% 8%',
  'sidebar-accent': '30 10% 16%',
  'sidebar-accent-foreground': '40 8% 92%',
  'sidebar-border': '30 10% 16%',
  'sidebar-ring': '165 25% 52%',
};

// ─── PREMIUM THEMES ─────────────────────────────────────

const forestCanopyTokens: ThemeTokens = {
  background: '140 20% 94%',
  foreground: '150 22% 12%',
  card: '140 14% 98%',
  'card-foreground': '150 22% 12%',
  popover: '140 14% 98%',
  'popover-foreground': '150 22% 12%',
  primary: '150 35% 28%',
  'primary-foreground': '0 0% 100%',
  secondary: '138 16% 91%',
  'secondary-foreground': '150 20% 20%',
  muted: '135 12% 89%',
  'muted-foreground': '150 10% 40%',
  accent: '45 65% 52%',
  'accent-foreground': '45 65% 12%',
  success: '150 40% 38%',
  'success-foreground': '0 0% 100%',
  warning: '42 70% 52%',
  'warning-foreground': '42 70% 12%',
  danger: '0 48% 55%',
  'danger-foreground': '0 0% 100%',
  destructive: '0 48% 55%',
  'destructive-foreground': '0 0% 100%',
  border: '138 12% 87%',
  input: '138 12% 87%',
  ring: '150 35% 28%',
  'sidebar-background': '150 28% 10%',
  'sidebar-foreground': '140 14% 95%',
  'sidebar-primary': '150 35% 45%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '150 22% 16%',
  'sidebar-accent-foreground': '140 14% 95%',
  'sidebar-border': '150 22% 16%',
  'sidebar-ring': '150 35% 45%',
};

const celestialTokens: ThemeTokens = {
  background: '250 28% 7%',
  foreground: '240 12% 92%',
  card: '250 22% 11%',
  'card-foreground': '240 12% 92%',
  popover: '250 22% 11%',
  'popover-foreground': '240 12% 92%',
  primary: '260 45% 58%',
  'primary-foreground': '260 45% 10%',
  secondary: '250 18% 15%',
  'secondary-foreground': '240 10% 88%',
  muted: '250 16% 13%',
  'muted-foreground': '240 8% 50%',
  accent: '210 55% 55%',
  'accent-foreground': '210 55% 10%',
  success: '165 42% 45%',
  'success-foreground': '165 42% 8%',
  warning: '45 65% 55%',
  'warning-foreground': '45 65% 10%',
  danger: '355 45% 58%',
  'danger-foreground': '0 0% 100%',
  destructive: '355 45% 58%',
  'destructive-foreground': '0 0% 100%',
  border: '250 20% 17%',
  input: '250 20% 17%',
  ring: '260 45% 58%',
  'sidebar-background': '250 32% 5%',
  'sidebar-foreground': '240 12% 90%',
  'sidebar-primary': '260 42% 62%',
  'sidebar-primary-foreground': '250 32% 5%',
  'sidebar-accent': '250 22% 11%',
  'sidebar-accent-foreground': '240 12% 90%',
  'sidebar-border': '250 22% 11%',
  'sidebar-ring': '260 42% 62%',
};

// ─── THEME REGISTRY ─────────────────────────────────────

export const THEME_DEFINITIONS: Record<string, ThemeDefinition> = {
  // Free
  sanctuary: {
    id: 'sanctuary',
    name: 'Sanctuary',
    description: 'Warm ivory with drifting light rays',
    tier: 'free',
    category: 'light',
    animation: 'light-rays',
    preview: {
      primary: 'hsl(160, 35%, 26%)',
      background: 'hsl(40, 20%, 96%)',
      accent: 'hsl(42, 80%, 52%)',
    },
    tokens: sanctuaryTokens,
  },
  meadow: {
    id: 'meadow',
    name: 'Meadow',
    description: 'Sage green with flowing leaves',
    tier: 'free',
    category: 'light',
    animation: 'flowing-leaves',
    preview: {
      primary: 'hsl(145, 30%, 32%)',
      background: 'hsl(130, 18%, 96%)',
      accent: 'hsl(80, 45%, 48%)',
    },
    tokens: meadowTokens,
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    description: 'Deep teal with subtle water ripples',
    tier: 'free',
    category: 'light',
    animation: 'ripple',
    preview: {
      primary: 'hsl(185, 40%, 28%)',
      background: 'hsl(190, 20%, 95%)',
      accent: 'hsl(170, 50%, 42%)',
    },
    tokens: oceanTokens,
  },
  dune: {
    id: 'dune',
    name: 'Dune',
    description: 'Sand beige with twirling wind',
    tier: 'free',
    category: 'light',
    animation: 'twirling-wind',
    preview: {
      primary: 'hsl(28, 30%, 38%)',
      background: 'hsl(32, 25%, 95%)',
      accent: 'hsl(35, 55%, 50%)',
    },
    tokens: duneTokens,
  },

  // Pro
  twilight: {
    id: 'twilight',
    name: 'Twilight',
    description: 'Deep navy with drifting stars',
    tier: 'pro',
    category: 'dark',
    animation: 'stars',
    preview: {
      primary: 'hsl(220, 55%, 55%)',
      background: 'hsl(225, 30%, 8%)',
      accent: 'hsl(210, 40%, 60%)',
    },
    tokens: twilightTokens,
  },
  aurora: {
    id: 'aurora',
    name: 'Aurora',
    description: 'Soft blues and lavender gradient waves',
    tier: 'pro',
    category: 'light',
    animation: 'gradient-wave',
    preview: {
      primary: 'hsl(220, 55%, 52%)',
      background: 'hsl(230, 28%, 97%)',
      accent: 'hsl(270, 45%, 62%)',
    },
    tokens: auroraTokens,
  },
  'zen-garden': {
    id: 'zen-garden',
    name: 'Zen Garden',
    description: 'Stone and sand with zen ripple patterns',
    tier: 'pro',
    category: 'light',
    animation: 'zen-ripple',
    preview: {
      primary: 'hsl(35, 20%, 40%)',
      background: 'hsl(40, 10%, 95%)',
      accent: 'hsl(165, 25%, 48%)',
    },
    tokens: zenGardenTokens,
  },

  // Premium
  'forest-canopy': {
    id: 'forest-canopy',
    name: 'Forest Canopy',
    description: 'Deep green with shifting canopy light',
    tier: 'premium',
    category: 'light',
    animation: 'canopy',
    preview: {
      primary: 'hsl(150, 35%, 28%)',
      background: 'hsl(140, 20%, 94%)',
      accent: 'hsl(45, 65%, 52%)',
    },
    tokens: forestCanopyTokens,
  },
  celestial: {
    id: 'celestial',
    name: 'Celestial',
    description: 'Deep space with floating particles',
    tier: 'premium',
    category: 'dark',
    animation: 'particles',
    preview: {
      primary: 'hsl(260, 45%, 58%)',
      background: 'hsl(250, 28%, 7%)',
      accent: 'hsl(210, 55%, 55%)',
    },
    tokens: celestialTokens,
  },
};

// Legacy theme ID mapping for graceful migration
const LEGACY_THEME_MAP: Record<string, string> = {
  'light-calm': 'sanctuary',
  'dark-comfort': 'twilight',
  'system-auto': 'sanctuary',
  'minimal-cream': 'dune',
  'serenity-pastel': 'aurora',
  'nature-earth': 'forest-canopy',
  'warm-sunrise': 'sanctuary',
  'cool-twilight': 'celestial',
  'zen-stone': 'zen-garden',
};

export function resolveLegacyTheme(themeId: string): string {
  return LEGACY_THEME_MAP[themeId] || themeId;
}

export const FREE_THEMES = Object.values(THEME_DEFINITIONS).filter(t => t.tier === 'free');
export const PRO_THEMES = Object.values(THEME_DEFINITIONS).filter(t => t.tier === 'pro');
export const PREMIUM_THEMES = Object.values(THEME_DEFINITIONS).filter(t => t.tier === 'premium');
export const ALL_THEMES = Object.values(THEME_DEFINITIONS);

export const DEFAULT_THEME = 'sanctuary';
export const DEFAULT_DARK_THEME = 'twilight';
